import {useState, useEffect, useCallback} from 'react';
import {useLocation} from 'react-router';
import {ToggleButton, Container, Spinner} from 'react-bootstrap';
import ReservationCard from '../components/ReservationCard';
import ReservationDetailModal from '../components/ReservationDetailModal';

import * as ReservationApi from '../api/ReservationApi';
import * as ComplaintApi from '../api/ComplaintApi';
import PageShell from '../components/PageShell';
import {problemMessage} from '../api/client';
import {hubReservationId} from '../consultation/deepLinks';
import {isReleased, payByDeadline, paymentDue} from '../consultation/bookingState';

/**
 * Consultation records. `?reservation=<id>` (from a deep link) opens that reservation's detail
 * straight away, whichever tab it would appear in.
 */
export default function ReservationHistory() {
  const openId = hubReservationId(useLocation().search);
  const radios = [
    {id: 0, name: 'Current Reservation', value: 'future'},
    {id: 1, name: 'Consultation record', value: 'history'},
  ];
  const [sequence, setSepuence] = useState('future');
  const [reservationArray, setReservationArray] = useState([]);
  const [retrieveReservations, setRetrieveReservations] = useState(false);
  const [reservation, setReservation] = useState();
  const [complaint, setComplaint] = useState();
  const [answers, setAnswers] = useState([]);
  const [reservationModalShow, setReservationModalShow] = useState(false);
  // Reservation whose payment arrived after it was released: its detail shows the refund notice.
  const [refundedId, setRefundedId] = useState(null);

  const [actionError, setActionError] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);

  const showReservationDetail = useCallback(async (reservationId) => {
    setActionError(null);
    let reservationData;
    let answersData;
    let complaintData;
    try {
      [reservationData, answersData, complaintData] = await Promise.all([
        ReservationApi.getReservation(reservationId),
        ReservationApi.getAnswers(reservationId),
        ComplaintApi.getComplaint(reservationId),
      ]);
    } catch (err) {
      setActionError(problemMessage(err, 'Could not load this consultation. Please try again.'));
      return;
    }
    setReservation(reservationData);
    // Shape expected by ReservationDetailModal.
    setAnswers(
      answersData.map((a) => ({
        ...a,
        form_and_questions: {order: a.order, question: {question_label: a.question_label || ''}},
      })),
    );
    setComplaint(complaintData);
    setReservationModalShow(true);
  }, []);

  useEffect(() => {
    if (openId) showReservationDetail(openId);
  }, [openId, showReservationDetail]);

  const cancelReservation = (reservationId) => {
    setActionError(null);
    ReservationApi.deleteReservation(reservationId)
      .then(() => {
        setReservationArray(
          reservationArray.filter((reservationData) => reservationData.id !== reservationId),
        );
        setReservationModalShow(false);
      })
      .catch((err) => {
        setReservationModalShow(false);
        setActionError(problemMessage(err, 'Could not cancel this consultation. Please try again.'));
      });
  };

  // Payment completed from the detail view: the open detail and the list both show it as paid.
  const markPaid = useCallback((reservationId) => {
    const mark = (r) =>
      r.id === reservationId
        ? {...r, payment: {fee: r.charge, is_refund: false, ...(r.payment || {}), is_paid: true}}
        : r;
    setReservation((current) => (current && current.id === reservationId ? mark(current) : current));
    setReservationArray((list) => list.map(mark));
  }, []);

  // The payment confirm answered 409: the consultation was released and the charge refunded. The
  // row is marked released at once, then refreshed from the server (a failed refresh keeps the mark).
  const markReleased = useCallback((reservationId) => {
    setRefundedId(reservationId);
    const mark = (r) => (r.id === reservationId ? {...r, status: 'released', is_cancelled: true} : r);
    setReservation((current) => (current && current.id === reservationId ? mark(current) : current));
    setReservationArray((list) => list.map(mark));
    ReservationApi.getReservation(reservationId)
      .then((fresh) => {
        const take = (r) => (r.id === reservationId ? fresh : r);
        setReservation((current) => (current && current.id === reservationId ? fresh : current));
        setReservationArray((list) => list.map(take));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setRetrieveReservations(false);
    setLoadError(false);
    ReservationApi.getReservationList(sequence)
      .then((reservations) => {
        setRetrieveReservations(true);
        if (Array.isArray(reservations)) {
          setReservationArray(reservations);
        } else {
          console.error('API returned non-array:', reservations);
          setReservationArray([]);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch reservations:', err);
        setLoadError(true);
        setRetrieveReservations(true);
        setReservationArray([]);
      });
  }, [sequence, reloadCount]);

  return (
    <PageShell title="Reservation History" showBackButton={true}>
      <Container>
        <div style={{padding: '1rem 0', textAlign: 'center'}}>
          {radios.map((radio) => (
            <ToggleButton
              key={radio.id}
              id={`radio-${radio.id}`}
              type="radio"
              variant="outline-primary"
              name="radio"
              value={radio.value}
              checked={sequence === radio.value}
              onChange={(e) => setSepuence(e.currentTarget.value)}
            >
              {radio.name}
            </ToggleButton>
          ))}
        </div>
        {actionError && (
          <p role="alert" className="text-danger" style={{whiteSpace: 'pre-line'}}>
            {actionError}
          </p>
        )}
        {loadError && (
          <div role="alert" className="text-danger">
            <p>We could not load your consultations.</p>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => setReloadCount((n) => n + 1)}
            >
              Try again
            </button>
          </div>
        )}
        {retrieveReservations && !loadError && reservationArray.length === 0 && (
          <p>No consultations here yet.</p>
        )}
        {retrieveReservations ? (
          <ul className="list-style-none">
            {reservationArray.map((reservation) => (
              <ReservationCard
                key={reservation.id}
                id={reservation.id}
                timezone={reservation.timezone}
                reservationTime={reservation.reservation_time}
                doctor={reservation.doctor_name || ''}
                hasVideoRoom={reservation.has_video_room && reservation.status === 'confirmed'}
                paymentDue={paymentDue(reservation)}
                payBy={payByDeadline(reservation)}
                released={isReleased(reservation)}
                showReservationDetail={showReservationDetail}
              />
            ))}
          </ul>
        ) : (
          <div style={{textAlign: 'center'}}>
            <Spinner animation="border" role="status" variant="dark">
              <span className="visually-hidden">Loading consultations…</span>
            </Spinner>
          </div>
        )}
      </Container>
      <ReservationDetailModal
        show={reservationModalShow}
        onHide={() => setReservationModalShow(false)}
        reservation={reservation}
        answers={answers}
        complaint={complaint}
        refunded={Boolean(reservation) && refundedId === reservation.id}
        cancelReservation={cancelReservation}
        onPaid={markPaid}
        onReleased={markReleased}
      />
    </PageShell>
  );
}
